/**
 * PWA Hook - Progressive Web App State Management
 *
 * Provides a React hook for managing PWA state including:
 * - Install prompt handling
 * - Online/offline status
 * - Update notifications
 * - Service worker lifecycle
 */

import { useState, useEffect, useCallback } from 'react';
import {
  registerServiceWorker,
  skipWaiting,
  canInstall,
  showInstallPrompt,
  isInstalledPWA,
  initInstallPrompt,
  setupNetworkListeners,
  checkPWASupport,
  setAppBadge,
  clearAppBadge,
  shareContent,
  type PWASupport,
} from '../lib/pwa';

export interface PWAState {
  // Installation state
  isInstalled: boolean;
  canInstall: boolean;
  isInstalling: boolean;

  // Network state
  isOnline: boolean;
  isOfflineReady: boolean;

  // Update state
  hasUpdate: boolean;
  isUpdating: boolean;

  // Service worker state
  isRegistered: boolean;
  registration: ServiceWorkerRegistration | null;

  // Feature support
  support: PWASupport;
}

export interface PWAActions {
  // Install the PWA
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;

  // Apply pending update
  applyUpdate: () => void;

  // Dismiss update notification
  dismissUpdate: () => void;

  // Set notification badge
  setBadge: (count: number) => Promise<void>;

  // Clear notification badge
  clearBadge: () => Promise<void>;

  // Share content
  share: (data: { title?: string; text?: string; url?: string }) => Promise<boolean>;
}

export function usePWA(): [PWAState, PWAActions] {
  const [state, setState] = useState<PWAState>({
    isInstalled: isInstalledPWA(),
    canInstall: false,
    isInstalling: false,
    isOnline: navigator.onLine,
    isOfflineReady: false,
    hasUpdate: false,
    isUpdating: false,
    isRegistered: false,
    registration: null,
    support: checkPWASupport(),
  });

  // Initialize install prompt capture
  useEffect(() => {
    initInstallPrompt();

    const handleInstallAvailable = () => {
      setState((prev) => ({ ...prev, canInstall: true }));
    };

    const handleInstalled = () => {
      setState((prev) => ({
        ...prev,
        isInstalled: true,
        canInstall: false,
        isInstalling: false,
      }));
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);

    // Check if install is already available
    if (canInstall()) {
      handleInstallAvailable();
    }

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  // Register service worker
  useEffect(() => {
    const register = async () => {
      const registration = await registerServiceWorker({
        onUpdate: (reg) => {
          console.log('[usePWA] Update available');
          setState((prev) => ({
            ...prev,
            hasUpdate: true,
            registration: reg,
          }));
        },
        onSuccess: (reg) => {
          console.log('[usePWA] Offline ready');
          setState((prev) => ({
            ...prev,
            isOfflineReady: true,
            isRegistered: true,
            registration: reg,
          }));
        },
      });

      if (registration) {
        setState((prev) => ({
          ...prev,
          isRegistered: true,
          registration,
        }));
      }
    };

    register();
  }, []);

  // Setup network listeners
  useEffect(() => {
    const cleanup = setupNetworkListeners(
      () => setState((prev) => ({ ...prev, isOnline: false })),
      () => setState((prev) => ({ ...prev, isOnline: true }))
    );

    return cleanup;
  }, []);

  // Actions
  const install = useCallback(async () => {
    setState((prev) => ({ ...prev, isInstalling: true }));

    try {
      const result = await showInstallPrompt();

      if (result === 'accepted') {
        setState((prev) => ({
          ...prev,
          isInstalling: false,
          canInstall: false,
        }));
      } else {
        setState((prev) => ({ ...prev, isInstalling: false }));
      }

      return result;
    } catch (error) {
      setState((prev) => ({ ...prev, isInstalling: false }));
      throw error;
    }
  }, []);

  const applyUpdate = useCallback(() => {
    setState((prev) => ({ ...prev, isUpdating: true }));
    skipWaiting();

    // Reload after a short delay to allow the service worker to activate
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  const dismissUpdate = useCallback(() => {
    setState((prev) => ({ ...prev, hasUpdate: false }));
  }, []);

  const setBadge = useCallback(async (count: number) => {
    await setAppBadge(count);
  }, []);

  const clearBadgeAction = useCallback(async () => {
    await clearAppBadge();
  }, []);

  const share = useCallback(
    async (data: { title?: string; text?: string; url?: string }) => {
      return shareContent(data);
    },
    []
  );

  const actions: PWAActions = {
    install,
    applyUpdate,
    dismissUpdate,
    setBadge,
    clearBadge: clearBadgeAction,
    share,
  };

  return [state, actions];
}

export default usePWA;
